// Project settings (/p/:key/settings?tab=): streams, workflows, repositories,
// integrations, policy, and the project's name and key.

import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { PROJECT_KEY_RE, type Project, type SpecMode } from '@goblic/shared'
import { projectsApi, type OpenspecStatus } from '../../lib/api-client'
import { useWorkspaceId } from '../../lib/auth'
import { RequireProject } from '../../lib/project-context'
import { Field, Notice, PageHeader, Panel, Tabs } from '../../components/ui'
import { StreamSettingsPanel } from '../../components/board/stream-settings'
import { ProjectWorkflowsTab } from '../../components/projects/workflows-tab'
import { PlaneIntegrationTab } from '../../components/projects/plane-integration'
import { PolicyEditor, ProjectOverview } from '../projects'

export type ProjectSettingsTab =
  | 'streams'
  | 'workflows'
  | 'repositories'
  | 'integrations'
  | 'policy'
  | 'general'

export const PROJECT_SETTINGS_TABS: readonly ProjectSettingsTab[] = [
  'streams',
  'workflows',
  'repositories',
  'integrations',
  'policy',
  'general',
]

/** `/p/$projectKey/settings` search params (router.tsx). */
export interface ProjectSettingsSearch {
  tab?: ProjectSettingsTab
}

export function ProjectSettingsPage() {
  return (
    <RequireProject>
      {(project) => <SettingsContent project={project} />}
    </RequireProject>
  )
}

function SettingsContent({ project }: { project: Project }) {
  const navigate = useNavigate()
  const search = useSearch({ from: '/_authenticated/p/$projectKey/settings' })
  const tab = search.tab ?? 'streams'

  return (
    <div className="space-y-5">
      <PageHeader
        title="Project settings"
        description={`${project.name} (${project.key}): streams, workflows, repositories, rules and naming.`}
      />
      <Tabs<ProjectSettingsTab>
        label="Project settings"
        value={tab}
        onChange={(next) =>
          void navigate({
            to: '/p/$projectKey/settings',
            params: { projectKey: project.key },
            search: { tab: next },
            replace: true,
          })
        }
        options={[
          { value: 'streams', label: 'Streams' },
          { value: 'workflows', label: 'Workflows' },
          { value: 'repositories', label: 'Repositories' },
          { value: 'integrations', label: 'Integrations' },
          { value: 'policy', label: 'Policy' },
          { value: 'general', label: 'General' },
        ]}
      />
      {tab === 'streams' ? (
        <StreamSettingsPanel project={project} />
      ) : tab === 'workflows' ? (
        <ProjectWorkflowsTab project={project} />
      ) : tab === 'repositories' ? (
        <ProjectOverview project={project} />
      ) : tab === 'integrations' ? (
        <PlaneIntegrationTab project={project} />
      ) : tab === 'policy' ? (
        <PolicyEditor project={project} />
      ) : (
        <GeneralTab project={project} />
      )}
    </div>
  )
}

function GeneralTab({ project }: { project: Project }) {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState(project.name)
  const [key, setKey] = useState(project.key)
  const [description, setDescription] = useState(project.description ?? '')
  const [saved, setSaved] = useState(false)

  const trimmedKey = key.trim().toUpperCase()
  const keyValid = PROJECT_KEY_RE.test(trimmedKey)
  const keyChanged = trimmedKey !== project.key
  const dirty =
    name.trim() !== project.name ||
    keyChanged ||
    description.trim() !== (project.description ?? '')

  const saveMutation = useMutation({
    mutationFn: () =>
      projectsApi.update(project.id, workspaceId, {
        ...(name.trim() !== project.name ? { name: name.trim() } : {}),
        ...(keyChanged ? { key: trimmedKey } : {}),
        ...(description.trim() !== (project.description ?? '')
          ? { description: description.trim() || null }
          : {}),
      }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      setSaved(true)
      // The URL carries the key: follow it.
      if (updated.key !== project.key)
        void navigate({
          to: '/p/$projectKey/settings',
          params: { projectKey: updated.key },
          search: { tab: 'general' },
          replace: true,
        })
    },
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    setSaved(false)
    if (dirty && keyValid && name.trim()) saveMutation.mutate()
  }

  return (
    <div className="space-y-5">
      <Panel
        title="General"
        description="The name and the short key of the project."
      >
        <form className="max-w-lg space-y-5" onSubmit={handleSubmit}>
          <Field label="Name" htmlFor="project-name">
            <input
              id="project-name"
              className="field-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </Field>
          <Field
            label="Key"
            htmlFor="project-key"
            hint="2 to 10 capital letters or digits, starting with a letter."
          >
            <input
              id="project-key"
              className="field-input font-mono uppercase"
              value={key}
              maxLength={10}
              onChange={(event) => setKey(event.target.value)}
              required
            />
          </Field>
          {keyChanged ? (
            <Notice tone="signal">
              The key is part of every project URL (/p/{trimmedKey}/…) and of
              ticket keys such as {trimmedKey}-190, wherever a stream uses the
              project key as its prefix. Bookmarks and links that use the old
              key stop working.
            </Notice>
          ) : null}
          {!keyValid ? (
            <p className="text-sm text-danger">That isn’t a valid key.</p>
          ) : null}
          <Field label="Description" htmlFor="project-description">
            <textarea
              id="project-description"
              className="field-input"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
          {saveMutation.isError ? (
            <Notice tone="error">
              {saveMutation.error instanceof Error
                ? saveMutation.error.message
                : 'Couldn’t save the project.'}
            </Notice>
          ) : null}
          {saved && !dirty ? <Notice tone="success">Saved.</Notice> : null}
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn-primary"
              disabled={
                !dirty || !keyValid || !name.trim() || saveMutation.isPending
              }
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Panel>
      <SpecModePanel project={project} workspaceId={workspaceId} />
      <DeleteProjectPanel project={project} workspaceId={workspaceId} />
    </div>
  )
}

const SPEC_MODE_OPTIONS: { value: SpecMode; label: string; hint: string }[] = [
  {
    value: 'none',
    label: 'None',
    hint: 'Agents work from the task alone.',
  },
  {
    value: 'openspec',
    label: 'OpenSpec',
    hint: 'Agents write a change proposal in the repository’s openspec/ folder, implement it, and archive it in the same pull request.',
  },
]

/** What the repository check says about `openspec/`, and which template to run. */
function OpenspecHint({
  status,
  loading,
  onCheckAgain,
}: {
  status: OpenspecStatus | undefined
  loading: boolean
  onCheckAgain: () => void
}) {
  if (loading) return <Notice tone="signal">Checking the repository for an openspec/ folder…</Notice>
  return (
    <Notice tone="signal">
      {status?.state === 'present' ? (
        <>
          An openspec/ folder already exists in {status.repo} ({status.ref}), so
          the init template isn’t needed. Use the “Feature (OpenSpec)” template
          for new features.
        </>
      ) : status?.state === 'absent' ? (
        <>
          {status.repo} ({status.ref}) has no openspec/ folder yet. Run the
          “OpenSpec init” workflow template once to set it up, then use the
          “Feature (OpenSpec)” template for new features.
        </>
      ) : (
        <>
          {status?.state === 'unavailable' ? `${status.message} ` : ''}Run the
          “OpenSpec init” workflow template once if the repository has no
          openspec/ folder, and use the “Feature (OpenSpec)” template for new
          features.
        </>
      )}{' '}
      Switching this on does not change existing workflows.{' '}
      <button type="button" className="underline" onClick={onCheckAgain}>
        Check again
      </button>
    </Notice>
  )
}

/**
 * The project's specification method. OpenSpec only changes what agents are
 * told (and gives the sandbox the `openspec` command); the flow itself comes
 * from a workflow, such as the built-in OpenSpec templates.
 */
export function SpecModePanel({
  project,
  workspaceId,
}: {
  project: Project
  workspaceId: string
}) {
  const queryClient = useQueryClient()
  const [specMode, setSpecMode] = useState<SpecMode>(project.specMode)
  const [saved, setSaved] = useState(false)
  const dirty = specMode !== project.specMode
  const selected = SPEC_MODE_OPTIONS.find((option) => option.value === specMode)

  // Asked as soon as OpenSpec is chosen (before saving), so the page can say
  // whether the init template is still needed.
  const status = useQuery({
    queryKey: ['project', project.id, 'openspec-status', workspaceId],
    queryFn: () => projectsApi.openspecStatus(project.id, workspaceId),
    enabled: specMode === 'openspec',
    staleTime: 60_000,
    retry: false,
  })

  const saveMutation = useMutation({
    mutationFn: () => projectsApi.update(project.id, workspaceId, { specMode }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      setSaved(true)
    },
  })

  return (
    <Panel
      title="Specification method"
      description="How changes to this project are specified before they are built."
    >
      <form
        className="max-w-lg space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          setSaved(false)
          if (dirty) saveMutation.mutate()
        }}
      >
        <Field
          label="Method"
          htmlFor="project-spec-mode"
          hint={selected?.hint}
        >
          <select
            id="project-spec-mode"
            className="field-input"
            value={specMode}
            onChange={(event) => {
              setSaved(false)
              setSpecMode(event.target.value as SpecMode)
            }}
          >
            {SPEC_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        {specMode === 'openspec' ? (
          <OpenspecHint status={status.data} loading={status.isLoading} onCheckAgain={() => void status.refetch()} />
        ) : null}
        {saveMutation.isError ? (
          <Notice tone="error">
            {saveMutation.error instanceof Error
              ? saveMutation.error.message
              : 'Couldn’t save the setting.'}
          </Notice>
        ) : null}
        {saved && !dirty ? <Notice tone="success">Saved.</Notice> : null}
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-primary"
            disabled={!dirty || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Panel>
  )
}

function DeleteProjectPanel({
  project,
  workspaceId,
}: {
  project: Project
  workspaceId: string
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [confirmKey, setConfirmKey] = useState('')
  const confirmed = confirmKey.trim().toUpperCase() === project.key

  const deleteMutation = useMutation({
    mutationFn: () => projectsApi.remove(project.id, workspaceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.removeQueries({ queryKey: ['project', project.id] })
      void navigate({ to: '/dashboard' })
    },
  })

  return (
    <Panel
      title="Delete project"
      description="Permanently removes the project with its tasks, runs, streams, policies, integrations and knowledge pages."
    >
      <form
        className="max-w-lg space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (confirmed) deleteMutation.mutate()
        }}
      >
        <Notice tone="signal">
          This cannot be undone. Agents scoped to this project are switched off
          and its connections are revoked. Runs that are still in progress have
          to be cancelled first.
        </Notice>
        <Field
          label={`Type ${project.key} to confirm`}
          htmlFor="project-delete-confirm"
        >
          <input
            id="project-delete-confirm"
            className="field-input font-mono uppercase"
            value={confirmKey}
            onChange={(event) => setConfirmKey(event.target.value)}
            autoComplete="off"
          />
        </Field>
        {deleteMutation.isError ? (
          <Notice tone="error">
            {deleteMutation.error instanceof Error
              ? deleteMutation.error.message
              : 'Couldn’t delete the project.'}
          </Notice>
        ) : null}
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-danger"
            disabled={!confirmed || deleteMutation.isPending}
          >
            {deleteMutation.isPending
              ? 'Deleting…'
              : 'Delete project permanently'}
          </button>
        </div>
      </form>
    </Panel>
  )
}
